import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";

const links = [
    { to: "/", label: "Home" },
    { to: "/packages", label: "Menu Packages" },
    { to: "/weddings", label: "Weddings" },
    { to: "/book", label: "Reserve" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export default function NavBar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="main-nav"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled ? "glass-nav py-3" : "bg-transparent py-5"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="logo-link"
                    className="flex items-center gap-3 group"
                >
                    <img
                        src={scrolled ? "/coral-bay-logo-dark.png" : "/coral-bay-logo-light.png"}
                        alt="Coral Bay Catering"
                        className="h-12 lg:h-14 w-auto transition-opacity duration-500"
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-10">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `underline-link text-sm tracking-wide ${
                                    scrolled
                                        ? isActive
                                            ? "text-forest-700"
                                            : "text-forest-700/70 hover:text-forest-700"
                                        : isActive
                                          ? "text-white"
                                          : "text-white/80 hover:text-white"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <a
                    href="tel:3404236611"
                    data-testid="nav-call-btn"
                    className={`hidden lg:inline-flex items-center gap-2 text-sm tracking-wide rounded-sm px-5 py-2.5 transition-all ${
                        scrolled
                            ? "bg-forest-700 text-sand-100 hover:bg-forest-900"
                            : "bg-white/90 text-forest-700 hover:bg-white"
                    }`}
                >
                    <Phone className="w-4 h-4" strokeWidth={1.5} />
                    340-423-6611
                </a>

                <button
                    onClick={() => setOpen((v) => !v)}
                    data-testid="mobile-menu-toggle"
                    className={`lg:hidden p-2 ${scrolled ? "text-forest-700" : "text-white"}`}
                    aria-label="Toggle menu"
                >
                    {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden glass-nav border-t border-white/40 px-6 py-6">
                    <div className="flex flex-col gap-4">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `text-base ${isActive ? "text-forest-700" : "text-forest-700/70"}`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <a
                            href="tel:3404236611"
                            data-testid="mobile-call-btn"
                            className="inline-flex items-center gap-2 bg-forest-700 text-sand-100 rounded-sm px-5 py-3 w-fit"
                        >
                            <Phone className="w-4 h-4" /> 340-423-6611
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
